<template>
    <span class="property-media-preview-bridge" style="display:none" aria-hidden="true"></span>
</template>

<script>
import {
    buildFancyboxItems,
    buildDocumentFancyboxItem,
    openPropertyGallery,
    openSinglePropertyImage,
    extractPropertyPhotoFilename,
} from '../lib/fancyboxPropertyGallery';

var DRAG_THRESHOLD_PX = 5;

function normalizeUrl(value) {
    if (typeof value !== 'string' || value.length === 0) return '';
    return value.split('#')[0].split('?')[0].replace(/\/+$/, '');
}

// Resolve the gallery index for a promo URL using a strict priority chain:
//   1. exact src match against the built items
//   2. normalized alternate-URL match against each record's preview/thumbnail URL
//   3. filename match (same source file behind different transform URLs)
//   4. -1 when nothing matches (caller falls back to single-image viewer)
// Iteration follows the record array order, so main photos beat other photos
// and (if multiple match) is_profile/primary wins inside the main list because
// the parent already orders the collection by priority.
function locatePhotoSlide(items, photoRecords, url) {
    if (typeof url !== 'string' || url.length === 0) return -1;
    var normalized = normalizeUrl(url);
    var targetFilename = extractPropertyPhotoFilename(url);

    for (var i = 0; i < items.length; i++) {
        if (items[i].src === url) return i;
    }

    var primaryHit = -1;
    var filenameHit = -1;
    var photoCursor = 0;
    var primaryPreference = function (rec) {
        return rec && (rec.is_profile === true || rec.is_profile === 1 || rec.is_profile === '1');
    };

    for (var k = 0; k < photoRecords.length; k++) {
        var rec = photoRecords[k];
        if (!rec || !rec.previewUrl) continue;
        var recPreview = normalizeUrl(rec.previewUrl);
        var recThumb   = normalizeUrl(rec.thumbnailUrl);
        var alt = Array.isArray(rec.alternateUrls) ? rec.alternateUrls : [];
        var altHit = (recPreview && recPreview === normalized)
                  || (recThumb   && recThumb   === normalized);
        if (!altHit) {
            for (var a = 0; a < alt.length; a++) {
                if (normalizeUrl(alt[a]) === normalized) { altHit = true; break; }
            }
        }
        if (altHit) {
            if (primaryPreference(rec)) return photoCursor;
            if (primaryHit < 0) primaryHit = photoCursor;
        } else if (targetFilename && filenameHit < 0) {
            if (extractPropertyPhotoFilename(rec.previewUrl) === targetFilename) {
                filenameHit = photoCursor;
            } else if (extractPropertyPhotoFilename(rec.thumbnailUrl) === targetFilename) {
                filenameHit = photoCursor;
            } else {
                for (var b = 0; b < alt.length; b++) {
                    if (extractPropertyPhotoFilename(alt[b]) === targetFilename) {
                        filenameHit = photoCursor;
                        break;
                    }
                }
            }
        }
        photoCursor++;
    }

    if (primaryHit >= 0) return primaryHit;
    if (filenameHit >= 0) return filenameHit;
    return -1;
}

var IGNORE_SELECTORS = [
    'button',
    '.theme-gallery__close',
    '.theme-gallery__done',
    '.theme-gallery__failed-overlay',
    '.theme-gallery__status',
    '.theme-gallery__item-to-drop',
].join(',');
var GALLERY_HOLDER_SELECTOR = '.theme-multiple-files__holder, .rental-form-video-uploader, .property-edit-photos-holder, .property-edit-other-photos-holder, .property-edit-documents-holder';

export default {
    props: {
        getPhotos: { type: Function, default: null },
        getVideos: { type: Function, default: null },
        getOtherPhotos: { type: Function, default: null },
        getDocuments: { type: Function, default: null },
    },
    data: function () {
        return {
            pointerDownX: null,
            pointerDownY: null,
            suppressNextClick: false,
        };
    },
    mounted: function () {
        document.addEventListener('pointerdown', this.onPointerDown, true);
        document.addEventListener('pointerup', this.onPointerUp, true);
        document.addEventListener('click', this.onDocClick, true);
    },
    beforeDestroy: function () {
        document.removeEventListener('pointerdown', this.onPointerDown, true);
        document.removeEventListener('pointerup', this.onPointerUp, true);
        document.removeEventListener('click', this.onDocClick, true);
    },
    methods: {
        onPointerDown: function (e) {
            var target = e && e.target;
            if (!target || typeof target.closest !== 'function') return;
            if (!target.closest(GALLERY_HOLDER_SELECTOR)) return;
            this.pointerDownX = e.clientX;
            this.pointerDownY = e.clientY;
            this.suppressNextClick = false;
        },
        onPointerUp: function (e) {
            if (this.pointerDownX === null) return;
            var dx = Math.abs(e.clientX - this.pointerDownX);
            var dy = Math.abs(e.clientY - this.pointerDownY);
            if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
                this.suppressNextClick = true;
            }
            this.pointerDownX = null;
            this.pointerDownY = null;
        },
        onDocClick: function (e) {
            if (this.suppressNextClick) {
                this.suppressNextClick = false;
                return;
            }
            var target = e && e.target;
            if (!target || typeof target.closest !== 'function') return;

            var inner = target.closest('.theme-gallery__item-inner');
            if (!inner) return;
            if (target.closest(IGNORE_SELECTORS)) return;

            var otherHolder = inner.closest('.property-edit-other-photos-holder');
            var photoHolder = !otherHolder ? inner.closest('.theme-multiple-files__holder, .property-edit-photos-holder') : null;
            var videoHolder = inner.closest('.rental-form-video-uploader');
            
            var documentHolder = inner.closest('.property-edit-documents-holder');
            if (!photoHolder && !videoHolder && !otherHolder && !documentHolder) return;

            var li = inner.closest('li.theme-gallery__item--draggable');
            if (!li || !li.parentElement) return;

            var siblings = li.parentElement.querySelectorAll('li.theme-gallery__item--draggable');
            var domIdx = -1;
            for (var i = 0; i < siblings.length; i++) {
                if (siblings[i] === li) { domIdx = i; break; }
            }
            if (domIdx < 0) return;

            // Document clicks are wired directly via Vue @click on the thumbnail
            // (see openPropertyDocumentPreview); the doc holder only joins the selector
            // so pointerdown drag detection skips the document gallery too.
            if (documentHolder) return;

            var li = inner.closest('li.theme-gallery__item--draggable');
            if (!li || !li.parentElement) return;

            var siblings = li.parentElement.querySelectorAll('li.theme-gallery__item--draggable');
            var domIdx = -1;
            for (var i = 0; i < siblings.length; i++) {
                if (siblings[i] === li) { domIdx = i; break; }
            }
            if (domIdx < 0) return;

            if (otherHolder) {
                var otherPhotos = this.resolveOtherPhotos();
                var otherItems = buildFancyboxItems(otherPhotos, []);
                if (!otherItems.length) return;
                var oAcc = 0;
                var oStart = -1;
                for (var op = 0; op < otherPhotos.length && op <= domIdx; op++) {
                    var orec = otherPhotos[op];
                    var oHas = orec && orec.previewUrl;
                    if (op === domIdx) {
                        if (!oHas) return;
                        oStart = oAcc;
                        break;
                    }
                    if (oHas) oAcc++;
                }
                if (oStart < 0) return;
                e.preventDefault();
                e.stopPropagation();
                openPropertyGallery(otherItems, oStart);
                return;
            }

            var photos = this.resolvePhotos();
            var videos = this.resolveVideos();
            var items = buildFancyboxItems(photos, videos);
            if (!items.length) return;

            var startIndex = -1;

            if (photoHolder) {
                var acc = 0;
                for (var p = 0; p < photos.length && p <= domIdx; p++) {
                    var rec = photos[p];
                    var hasPreview = rec && rec.previewUrl;
                    if (p === domIdx) {
                        if (!hasPreview) return;
                        startIndex = acc;
                        break;
                    }
                    if (hasPreview) acc++;
                }
            } else {
                var clickedTile = videos[domIdx];
                if (!clickedTile || clickedTile.status !== 'ready' || !clickedTile.url) return;
                var photoItemsCount = 0;
                for (var k = 0; k < items.length; k++) {
                    if (items[k].type === 'image') photoItemsCount++;
                }
                var vAcc = 0;
                for (var v = 0; v < videos.length && v <= domIdx; v++) {
                    var t = videos[v];
                    var ready = t && t.status === 'ready' && t.url;
                    if (v === domIdx) {
                        startIndex = photoItemsCount + vAcc;
                        break;
                    }
                    if (ready) vAcc++;
                }
            }

            if (startIndex < 0) return;

            e.preventDefault();
            e.stopPropagation();
            openPropertyGallery(items, startIndex);
        },
        openPropertyDocumentPreview: function (record) {
            var item = buildDocumentFancyboxItem(record);
            if (!item) {
                // Unsupported document type — best-effort: open the file URL directly.
                var fallbackUrl = record && (record.fileUrl || record.file_url || record.url);
                if (typeof fallbackUrl === 'string' && fallbackUrl.length > 0) {
                    try { window.open(fallbackUrl, '_blank', 'noopener'); } catch (_) {}
                }
                return false;
            }
            return openPropertyGallery([item], 0);
        },
        openCombinedGallery: function (startIndex) {
            var photos = this.resolvePhotos();
            var videos = this.resolveVideos();
            var items = buildFancyboxItems(photos, videos);
            if (!items.length) return;
            var idx = parseInt(startIndex, 10);
            if (isNaN(idx) || idx < 0) idx = 0;
            openPropertyGallery(items, idx);
        },
        openCombinedGalleryAtSrc: function (url) {
            var photos = this.resolvePhotos();
            var otherPhotos = this.resolveOtherPhotos();
            var videos = this.resolveVideos();
            // Combined surface: main photos + other photos + videos so the promo image can
            // surface a match no matter where the underlying file lives. Main first so
            // multi-candidate filename matches resolve to a primary slide.
            var allPhotos = photos.concat(otherPhotos);
            var items = buildFancyboxItems(allPhotos, videos);
            var startIndex = -1;

            if (items.length > 0 && typeof url === 'string' && url.length > 0) {
                startIndex = locatePhotoSlide(items, allPhotos, url);
            }

            if (items.length > 0) {
                if (startIndex < 0) startIndex = 0;
                openPropertyGallery(items, startIndex);
                return;
            }
            // Fallback: no resolvable gallery (e.g. property has no photos yet) but the
            // promo URL itself is valid. Open it as a single-image viewer.
            openSinglePropertyImage(url);
        },
        resolvePhotos: function () {
            try {
                if (typeof this.getPhotos !== 'function') return [];
                var ref = this.getPhotos();
                if (!ref || typeof ref.getSelectedFileRecords !== 'function') return [];
                var recs = ref.getSelectedFileRecords();
                return Array.isArray(recs) ? recs : [];
            } catch (err) {
                return [];
            }
        },
        resolveVideos: function () {
            try {
                if (typeof this.getVideos !== 'function') return [];
                var ref = this.getVideos();
                if (!ref || !Array.isArray(ref.tiles)) return [];
                return ref.tiles;
            } catch (err) {
                return [];
            }
        },
        
        resolveDocuments: function () {
            try {
                if (typeof this.getDocuments !== 'function') return [];
                var ref = this.getDocuments();
                if (!ref || typeof ref.getSelectedFileRecords !== 'function') return [];
                var recs = ref.getSelectedFileRecords();
                return Array.isArray(recs) ? recs : [];
            } catch (err) {
                return [];
            }
        },

        resolveOtherPhotos: function () {
            try {
                if (typeof this.getOtherPhotos !== 'function') return [];
                var ref = this.getOtherPhotos();
                if (!ref || typeof ref.getSelectedFileRecords !== 'function') return [];
                var recs = ref.getSelectedFileRecords();
                return Array.isArray(recs) ? recs : [];
            } catch (err) {
                return [];
            }
        },
    },
};
</script>
